import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const steps = [
  {
    num: '01',
    title: 'Dial in the round',
    body: 'Role, company vibe, seniority, interview type, difficulty — so pressure matches reality from minute one.',
    icon: 'SlidersHorizontal',
  },
  {
    num: '02',
    title: 'Get interviewed for real',
    body: 'Voice that interrupts, coding with follow-ups, design whiteboards — not a scripted FAQ bot.',
    icon: 'MessagesSquare',
  },
  {
    num: '03',
    title: 'Read the scorecard',
    body: 'Structure, depth, filler words, missed follow-ups — the same dimensions recruiters actually grade.',
    icon: 'ScanSearch',
  },
  {
    num: '04',
    title: 'Book the next session',
    body: 'Your weak spots become tomorrow’s calendar. Progress compounds instead of guessing what to drill.',
    icon: 'CalendarCheck',
  },
];

const HowItWorksSection = () => {
  return (
    <section
      id="how-it-works"
      className="relative bg-[var(--lp-contrast-bg)] py-24 text-[var(--lp-contrast-text)] lg:py-32"
      aria-labelledby="how-heading"
    >
      <div className="lp-grain absolute inset-0 opacity-[0.06]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-[0.45fr_1fr]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:sticky lg:top-28 lg:self-start"
          >
            <p className="font-mono text-[11px] font-semiboldtracking-[0.2em] text-[var(--lp-accent)]">
              How it works
            </p>
            <h2 id="how-heading" className="mt-4 text-4xl font-semibold leading-[1.05] tracking-tight text-[var(--lp-contrast-text)] sm:text-5xl">
              Four beats from
              <span className="block text-[var(--lp-contrast-text)]/50">nervous to ready.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[var(--lp-contrast-text)]/55">
              Most prep tools give you questions. We give you the conversation — then tell you exactly what to fix.
            </p>
          </motion.div>

          <div className="relative space-y-0">
            <div className="absolute bottom-8 left-[27px] top-8 w-px bg-[var(--lp-contrast-text)]/10 lg:left-[31px]" />
            {steps.map((step, index) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                className="relative flex gap-6 pb-12 last:pb-0 lg:gap-8"
              >
                <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[var(--lp-contrast-text)]/10 bg-[var(--lp-contrast-text)]/5 backdrop-blur">
                  <Icon name={step.icon} size={22} className="text-[var(--lp-accent)]" />
                </div>
                <div className="flex-1 border-b border-[var(--lp-contrast-text)]/10 pb-12 last:border-0">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="font-mono text-sm font-bold text-[var(--lp-accent)]">{step.num}</span>
                    <h3 className="text-2xl font-semibold tracking-tight text-[var(--lp-contrast-text)] sm:text-3xl">{step.title}</h3>
                  </div>
                  <p className="mt-3 max-w-xl text-sm leading-7 text-[var(--lp-contrast-text)]/55">{step.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
